import {
  useCallback, useContext, useEffect, useState,
} from 'react';
import usePageMeta from '../../hooks/usePageMeta';
import { AuthContext } from '../../context/AuthContext';
import LoadingSpinner from '../../components/global/LoadingSpinner';
import CharacterCustomizer from '../../components/profile/CharacterCustomizer';
import RoutePreview from '../../components/admin/RoutePreview';
import ScheduleWindow from '../../components/admin/ScheduleWindow';
import {
  listNpcs, updateNpc, deleteNpc, getMapGeometry, publishNpc, unpublishNpc,
} from '../../services/npcs';

/**
 * A conversation, as the form edits it.
 *
 * THE STORED SHAPE IS A GENERAL TREE — `{ id, text, choices: [{label, next}] }`
 * — but the form deliberately exposes only one level: a greeting, a question,
 * and a list of answers with replies. That is the whole of what an NPC needs to
 * "ask you something", it is authorable in a minute, and there is no way to
 * strand yourself in an unreachable node.
 *
 * Restricting the UI rather than the schema is the point: going deeper later
 * needs no migration and no re-authoring of anything written today.
 */
const toForm = (dialogue) => {
  const start = dialogue?.find((n) => n.id === 'start') || dialogue?.[0];
  if (!start) return { greeting: '', answers: [] };

  return {
    greeting: start.text || '',
    answers: (start.choices || []).map((c) => ({
      label: c.label || '',
      reply: dialogue.find((n) => n.id === c.next)?.text || '',
    })),
  };
};

const toDialogue = ({ greeting, answers }) => {
  if (!greeting.trim()) return [];

  const kept = answers.filter((a) => a.label.trim());

  return [
    {
      id: 'start',
      text: greeting,
      choices: kept.map((a, i) => ({ label: a.label, next: `a${i}` })),
    },
    // A reply node per answer. Ids are positional because the form has no
    // concept of a node — nothing else references them, so they only have to be
    // internally consistent.
    ...kept.map((a, i) => ({ id: `a${i}`, text: a.reply, choices: [] })),
  ];
};

/**
 * The cast.
 *
 * DELIBERATELY NOT A CREATE FORM. Characters are born in-game, by walking their
 * route — a route captured from where a character actually stood is walkable by
 * construction, and one typed into a form is a guess that fails silently (the
 * NPC spawns nudged clear of the wall it is aimed at, then spends its life on
 * the stuck-timeout fallback). This page is for everything that is easier
 * sitting still: naming, looks, purpose, speed, and switching one off.
 *
 * Appearance reuses the profile customizer rather than growing a second picker,
 * so NPCs and players cannot drift apart in what a character may look like.
 */
function AdminNpcs() {
  const { token } = useContext(AuthContext);
  usePageMeta({ title: 'NPCs', canonicalPath: '/admin/npcs' });

  const [npcs, setNpcs] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [saving, setSaving] = useState(false);
  // Map geometry for the preview, keyed by name. Cached because switching
  // between characters on the same map should not refetch it.
  const [maps, setMaps] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listNpcs(token);
      setNpcs(data.npcs || []);
    } catch (err) {
      setStatus({ error: err.message || 'Could not load NPCs.' });
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const loadMap = useCallback(async (mapName) => {
    if (!mapName || maps[mapName]) return;
    try {
      const geometry = await getMapGeometry(token, mapName);
      setMaps((prior) => ({ ...prior, [mapName]: geometry }));
    } catch {
      // A missing preview is not worth an error banner — the rest of the form
      // still works, and RoutePreview says "Loading map…" rather than breaking.
    }
  }, [token, maps]);

  const select = (npc) => {
    loadMap(npc.mapName);
    setSelectedId(npc._id);
    setStatus(null);
    setDraft({
      name: npc.name,
      purpose: npc.purpose || '',
      speed: npc.speed,
      enabled: npc.enabled,
      mode: npc.route?.mode || 'pingpong',
      appearance: npc.appearance || {},
      // Stored as an array, edited as a textarea — one line per line, which is
      // the obvious mapping and needs no separator to explain.
      ambientLines: (npc.ambientLines || []).join('\n'),
      ...toForm(npc.dialogue),
      route: npc.route,
      waypointCount: npc.route?.waypoints?.length || 0,
      scheduleMode: npc.schedule?.mode || 'always',
      days: npc.schedule?.days || [],
      from: npc.schedule?.from || '',
      to: npc.schedule?.to || '',
      timeZone: npc.schedule?.timeZone || 'America/New_York',
      mapName: npc.mapName,
    });
  };

  const save = async () => {
    setSaving(true);
    setStatus(null);
    try {
      await updateNpc(token, selectedId, {
        name: draft.name,
        purpose: draft.purpose,
        speed: Number(draft.speed),
        enabled: draft.enabled,
        appearance: draft.appearance,
        ambientLines: draft.ambientLines.split('\n'),
        dialogue: toDialogue(draft),
        schedule: {
          mode: draft.scheduleMode,
          days: draft.days,
          from: draft.from,
          to: draft.to,
          timeZone: draft.timeZone,
        },
      });
      setStatus({ ok: 'Saved — live rooms already updated.' });
      await load();
    } catch (err) {
      setStatus({ error: err.message || 'Could not save.' });
    } finally {
      setSaving(false);
    }
  };

  /**
   * Publish or pull back.
   *
   * Its own call rather than part of `save`, mirroring the server: introducing
   * somebody to every player is a deliberate act, not a consequence of editing
   * a field. `load()` afterwards because the badge has to come from the server's
   * answer, never from what we assumed it would be.
   */
  const setLive = async (npc, live) => {
    setStatus(null);
    try {
      await (live ? publishNpc : unpublishNpc)(token, npc._id);
      setStatus({
        ok: live
          ? `"${npc.name}" is live — everybody in the world can see them now.`
          : `"${npc.name}" is back to a draft. Only admins can see them.`,
      });
      await load();
    } catch (err) {
      setStatus({ error: err.message || 'Could not change who can see them.' });
    }
  };

  const remove = async (npc) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete "${npc.name}"? Their route is lost with them.`)) return;
    try {
      await deleteNpc(token, npc._id);
      if (npc._id === selectedId) { setSelectedId(null); setDraft(null); }
      await load();
    } catch (err) {
      setStatus({ error: err.message || 'Could not delete.' });
    }
  };

  if (loading) return <div className="p-8"><LoadingSpinner size="lg" /></div>;

  // The saved record, as distinct from `draft` — which is the unsaved form
  // state and has been called that since before NPCs had a draft status of
  // their own. Publishing acts on what is stored, not on what is typed.
  const selected = npcs.find((n) => n._id === selectedId) || null;

  return (
    <div className="mx-auto max-w-5xl p-6">
      <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">NPCs</h1>
      <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
        Create characters in-game: walk their route on
        {' '}
        <code>/play</code>
        {' '}
        and press R to drop each point. Edit who they are here.
      </p>

      {status?.error && <p className="mt-3 text-red-500">{status.error}</p>}
      {status?.ok && <p className="mt-3 text-emerald-500">{status.ok}</p>}

      <div className="mt-6 grid gap-6 md:grid-cols-[18rem_1fr]">
        <ul className="space-y-1">
          {npcs.length === 0 && (
            <li className="text-sm text-gray-500">
              Nobody yet — go and walk somebody a route.
            </li>
          )}
          {npcs.map((npc) => (
            <li key={npc._id}>
              <button
                type="button"
                onClick={() => select(npc)}
                className={`flex w-full items-center gap-2 rounded px-3 py-2 text-left text-sm ${
                  npc._id === selectedId
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-800 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700'
                }`}
              >
                <span className="font-medium">{npc.name}</span>
                <span className="opacity-60">{npc.mapName}</span>
                {/*
                  Two separate questions, so two separate badges: whether
                  players can see them at all, and whether they are out right
                  now. A draft on a weekday schedule is both "draft" and
                  "scheduled", and collapsing that into one label loses the half
                  you were checking.
                */}
                {npc.status === 'draft' && (
                  <span className="rounded bg-amber-500/20 px-1.5 py-0.5 text-xs text-amber-500">draft</span>
                )}
                {/*
                  activeNow comes from the SERVER, which owns the schedule
                  rules — so this badge and the live cast cannot disagree.
                */}
                <span className="ml-auto text-xs">
                  {!npc.enabled && <span className="opacity-70">off</span>}
                  {npc.enabled && npc.activeNow && <span className="text-emerald-400">● out now</span>}
                  {npc.enabled && !npc.activeNow && <span className="opacity-70">scheduled</span>}
                </span>
              </button>
            </li>
          ))}
        </ul>

        {draft ? (
          <div className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block text-sm" htmlFor="npc-name">
                <span className="text-gray-700 dark:text-gray-300">Name</span>
                <input
                  id="npc-name"
                  value={draft.name}
                  maxLength={16}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                  className="mt-1 w-full rounded border border-gray-300 px-2 py-1 dark:border-gray-700 dark:bg-gray-900"
                />
              </label>

              <label className="block text-sm" htmlFor="npc-speed">
                <span className="text-gray-700 dark:text-gray-300">Speed</span>
                <input
                  id="npc-speed"
                  type="number"
                  min="10"
                  max="200"
                  value={draft.speed}
                  onChange={(e) => setDraft({ ...draft, speed: e.target.value })}
                  className="mt-1 w-full rounded border border-gray-300 px-2 py-1 dark:border-gray-700 dark:bg-gray-900"
                />
                <span className="text-xs text-gray-500">
                  Players move at 110. Slower reads as an inhabitant.
                </span>
              </label>
            </div>

            <label className="block text-sm" htmlFor="npc-purpose">
              <span className="text-gray-700 dark:text-gray-300">
                Purpose
                <span className="ml-1 text-xs text-gray-500">(your note — players never see it)</span>
              </span>
              <textarea
                id="npc-purpose"
                rows={2}
                value={draft.purpose}
                onChange={(e) => setDraft({ ...draft, purpose: e.target.value })}
                className="mt-1 w-full rounded border border-gray-300 px-2 py-1 dark:border-gray-700 dark:bg-gray-900"
              />
            </label>

            <RoutePreview map={maps[draft.mapName]} route={draft.route} />
            <p className="text-xs text-gray-500">
              The route is the one thing this page cannot change. To alter a
              patrol, delete the character and walk a new one — which also loses
              their lines and conversation, so settle the route first.
            </p>

            <fieldset className="rounded border border-gray-300 p-3 dark:border-gray-700">
              <legend className="px-1 text-sm text-gray-700 dark:text-gray-300">
                When they are out
                <span className="ml-1 text-xs text-gray-500">
                  (times are
                  {' '}
                  {draft.timeZone}
                  )
                </span>
              </legend>

              <div className="flex gap-4 text-sm">
                {['always', 'weekly'].map((mode) => (
                  <label key={mode} className="flex items-center gap-1" htmlFor={`mode-${mode}`}>
                    <input
                      id={`mode-${mode}`}
                      type="radio"
                      name="scheduleMode"
                      checked={draft.scheduleMode === mode}
                      onChange={() => setDraft({ ...draft, scheduleMode: mode })}
                    />
                    {mode === 'always' ? 'Always' : 'On a schedule'}
                  </label>
                ))}
              </div>

              {draft.scheduleMode === 'weekly' && (
                <div className="mt-3 space-y-3">
                  {/*
                    One window, because a character is either out or they are
                    not. A business gets a list of these — see AdminBusinesses,
                    which renders the same component with add and remove.
                  */}
                  <ScheduleWindow
                    idPrefix="npc"
                    value={{ days: draft.days, from: draft.from, to: draft.to }}
                    onChange={(next) => setDraft({ ...draft, ...next })}
                  />

                  <p className="text-xs text-gray-500">
                    Leave the times blank for the whole day. A window that ends
                    before it starts runs overnight — 22:00 to 02:00 means late
                    on the days you picked and into the following morning.
                    Presence is checked every 30 seconds, so it is accurate to
                    about half a minute.
                  </p>
                </div>
              )}
            </fieldset>

            <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
              <label className="flex items-center gap-2" htmlFor="npc-enabled">
                <input
                  id="npc-enabled"
                  type="checkbox"
                  checked={draft.enabled}
                  onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })}
                />
                In the world
                <span className="text-xs text-gray-500">(overrides any schedule)</span>
              </label>
              <span>
                {draft.waypointCount}
                {' '}
                waypoints on
                {' '}
                {draft.mapName}
                {' '}
                (
                {draft.mode}
                )
              </span>
            </div>

            <label className="block text-sm" htmlFor="npc-lines">
              <span className="text-gray-700 dark:text-gray-300">
                Things they say
                <span className="ml-1 text-xs text-gray-500">
                  (one per line — said occasionally as they walk, only when
                  somebody is nearby to hear it)
                </span>
              </span>
              <textarea
                id="npc-lines"
                rows={4}
                value={draft.ambientLines}
                onChange={(e) => setDraft({ ...draft, ambientLines: e.target.value })}
                placeholder={'Lovely weather for it.\nMind the step.'}
                className="mt-1 w-full rounded border border-gray-300 px-2 py-1 font-mono text-xs dark:border-gray-700 dark:bg-gray-900"
              />
            </label>

            <fieldset className="rounded border border-gray-300 p-3 dark:border-gray-700">
              <legend className="px-1 text-sm text-gray-700 dark:text-gray-300">
                Conversation
                <span className="ml-1 text-xs text-gray-500">
                  (what they say when somebody presses E nearby)
                </span>
              </legend>

              <label className="block text-sm" htmlFor="npc-greeting">
                <span className="text-gray-600 dark:text-gray-400">
                  Greeting, or the question they ask
                </span>
                <textarea
                  id="npc-greeting"
                  rows={2}
                  value={draft.greeting}
                  onChange={(e) => setDraft({ ...draft, greeting: e.target.value })}
                  placeholder="You look lost. Where are you headed?"
                  className="mt-1 w-full rounded border border-gray-300 px-2 py-1 dark:border-gray-700 dark:bg-gray-900"
                />
                <span className="text-xs text-gray-500">
                  Leave this empty and they have nothing to say — no prompt appears.
                </span>
              </label>

              <div className="mt-3 space-y-2">
                {draft.answers.map((answer, i) => (
                  // Index is a stable key: rows are only appended and removed
                  // from the end, so a given row never moves within the list.
                  // eslint-disable-next-line react/no-array-index-key
                  <div key={i} className="grid gap-2 sm:grid-cols-2">
                    <input
                      value={answer.label}
                      onChange={(e) => setDraft({
                        ...draft,
                        answers: draft.answers.map((a, j) => (
                          j === i ? { ...a, label: e.target.value } : a)),
                      })}
                      placeholder={`Answer ${i + 1}`}
                      className="rounded border border-gray-300 px-2 py-1 text-sm dark:border-gray-700 dark:bg-gray-900"
                    />
                    <div className="flex gap-1">
                      <input
                        value={answer.reply}
                        onChange={(e) => setDraft({
                          ...draft,
                          answers: draft.answers.map((a, j) => (
                            j === i ? { ...a, reply: e.target.value } : a)),
                        })}
                        placeholder="What they say back"
                        className="w-full rounded border border-gray-300 px-2 py-1 text-sm dark:border-gray-700 dark:bg-gray-900"
                      />
                      <button
                        type="button"
                        onClick={() => setDraft({
                          ...draft,
                          answers: draft.answers.filter((a, j) => j !== i),
                        })}
                        className="rounded bg-gray-200 px-2 text-gray-600 hover:bg-gray-300 dark:bg-gray-800 dark:text-gray-400"
                        aria-label={`Remove answer ${i + 1}`}
                      >
                        ×
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <button
                type="button"
                onClick={() => setDraft({
                  ...draft,
                  answers: [...draft.answers, { label: '', reply: '' }],
                })}
                className="mt-2 rounded bg-gray-200 px-2 py-1 text-xs text-gray-700 hover:bg-gray-300 dark:bg-gray-800 dark:text-gray-300"
              >
                Add an answer
              </button>
            </fieldset>

            <CharacterCustomizer
              appearance={draft.appearance}
              onChange={(appearance) => setDraft({ ...draft, appearance })}
            />

            <div className="flex gap-2">
              <button
                type="button"
                onClick={save}
                disabled={saving}
                className="rounded bg-blue-600 px-4 py-2 text-white hover:bg-blue-500 disabled:opacity-50"
              >
                {saving ? 'Saving…' : 'Save'}
              </button>

              {/*
                The affirmative action, and the only green control on the page —
                the same weight Publish carries in the world editor, because it
                does the same thing: takes something only you could see and
                hands it to everybody, immediately.
              */}
              {selected?.status === 'draft' ? (
                <button
                  type="button"
                  onClick={() => setLive(selected, true)}
                  className="rounded bg-emerald-600 px-4 py-2 text-white hover:bg-emerald-500"
                >
                  Publish
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => setLive(selected, false)}
                  className="rounded bg-gray-200 px-4 py-2 text-gray-800 hover:bg-gray-300 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
                >
                  Unpublish
                </button>
              )}

              <button
                type="button"
                onClick={() => remove(selected)}
                className="rounded bg-red-700 px-4 py-2 text-white hover:bg-red-600"
              >
                Delete
              </button>
            </div>

            {selected?.status === 'draft' && (
              <p className="text-xs text-amber-500">
                A draft walks its route on the real map, but only admins can see
                them. Go and stand next to them before you publish.
              </p>
            )}
          </div>
        ) : (
          <p className="text-sm text-gray-500">Pick somebody to edit.</p>
        )}
      </div>
    </div>
  );
}

export default AdminNpcs;
